import React, { useState, useLayoutEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';

import { useDataContext } from 'src/context/data';
import analytics from 'src/utils/analytics/analytics';

import { isSinglePage } from 'src/utils/common';

import Page from './Page/Page';
import PageButton from './Page/PageButton';
import { usePlatform } from 'src/utils/hooks';

/* - - - - - - - - - - - - - - - - - - - - - - */

function Message({ isMessageAreaCanGetComponentHeightParameters }) {
  const {
    appState: {
      widgetData: {
        parameters: { isShowSiblingPageButtonsEnabled },
      },
      responsesCurrentBlobInfo,
      currentMessageId,
      currentMessage,
      currentPage,
      pages,
    },
    setResponsesGroupHidden,
    selectPage,
  } = useDataContext();

  const { isWindows } = usePlatform();

  const [wasMessageScrolled, setMessageWasScrolled] = useState<boolean>(false);

  const messageWrapperRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    setMessageWasScrolled(false);
  }, [currentMessageId]);

  useLayoutEffect(() => {
    const handleOnScroll = () => {
      if (messageWrapperRef.current) {
        if (!wasMessageScrolled && messageWrapperRef.current.scrollTop > 0) {
          setMessageWasScrolled(true);
        }
      }
    };

    messageWrapperRef?.current?.addEventListener('scroll', handleOnScroll);

    return () => {
      messageWrapperRef?.current?.removeEventListener('scroll', handleOnScroll);
    };
  });

  const renderScrollIndicator = useCallback(() => {
    if (!isWindows && messageWrapperRef.current && currentPage && currentPage.pageType !== 'carousel') {
      const isContentScrollable =
        messageWrapperRef.current.scrollHeight - Math.round(messageWrapperRef.current.scrollTop) - 1 >
        messageWrapperRef.current.clientHeight;
      if (isContentScrollable && !wasMessageScrolled && Math.round(messageWrapperRef.current.scrollTop) === 0) {
        return (
          <motion.button
            className="rcw-message-scroll-hint"
            onClick={() => {
              messageWrapperRef.current?.scrollTo({ top: messageWrapperRef.current.scrollHeight, behavior: 'smooth' });
              setMessageWasScrolled(true);
            }}
            whileHover={{ scale: 1.2 }}
          />
        );
      }
    }

    return null;
  }, [currentMessageId, currentPage]);

  const renderPageButton = page => {
    return (
      <PageButton
        key={'message-page-button-' + page.id}
        page={page}
        onClick={() => {
          analytics.trackPageButtonPressed({ pageId: page.id, pageCaption: page.caption });
          selectPage(page.id);
          setResponsesGroupHidden(true);
        }}
      ></PageButton>
    );
  };

  const renderPageButtons = () => {
    if (currentPage && isShowSiblingPageButtonsEnabled) {
      return pages.map(page => (page.id !== currentPage.id ? renderPageButton(page) : null));
    } else if (!currentPage) {
      return pages.map(page => renderPageButton(page));
    } else {
      return null;
    }
  };

  const renderMessage = () => {
    return (
      <div
        id={currentMessageId}
        ref={messageWrapperRef}
        className={`rcw-message-wrapper`}
        style={{ marginBottom: responsesCurrentBlobInfo.itemsWithin === 0 ? '10px' : 0 }}
        key={'message-wrapper-' + currentMessageId + '-' + (currentPage ? currentPage.id : '')}
      >
        <h3 className="rcw-message-title">{currentMessage.label}</h3>

        {currentPage && <Page key={'message-page-' + currentPage.id} page={currentPage} />}

        {!isSinglePage(pages) && currentPage && isShowSiblingPageButtonsEnabled && (
          <hr className="rcw-message-divider" />
        )}

        {renderPageButtons()}
      </div>
    );
  };

  return (
    <div className="rcw-message-container" data-cy="message-container">
      {renderMessage()}
      {renderScrollIndicator()}
    </div>
  );
}

export default Message;
